import { useEffect, useState } from "react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { BarChart3, MousePointerClick, Eye, ShoppingBag, Layers } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAnalytics() {
  const { admin } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/admin/analytics/summary`, {
      headers: { Authorization: `Bearer ${admin.token}` },
    })
      .then((res) => res.json())
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [admin.token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#333] border-t-[#D8C08F] rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-[#888] text-sm font-sans">Failed to load analytics.</p>;
  }

  return (
    <div data-testid="admin-analytics-page">
      <div className="flex items-center gap-3 mb-6">
        <BarChart3 size={20} className="text-[#D8C08F]" />
        <h1 className="text-white text-2xl font-sans font-semibold">Analytics</h1>
      </div>

      {/* Global stat */}
      <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-5 mb-8 inline-flex items-center gap-4">
        <div className="w-10 h-10 rounded-lg bg-[#D8C08F]/10 flex items-center justify-center">
          <Layers size={18} className="text-[#D8C08F]" />
        </div>
        <div>
          <p className="text-[#888] text-xs font-sans uppercase tracking-wider">Total App Opens</p>
          <p className="text-white text-2xl font-sans font-semibold" data-testid="total-app-opens">
            {data.totalAppOpens}
          </p>
        </div>
      </div>

      {/* Per-event table */}
      {data.events.length === 0 ? (
        <p className="text-[#666] text-sm font-sans">No analytics data yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="analytics-table">
            <thead>
              <tr className="border-b border-[#2a2a2a]">
                <th className="text-[#888] text-xs font-sans font-medium uppercase tracking-wider pb-3 pr-6">Event</th>
                <th className="text-[#888] text-xs font-sans font-medium uppercase tracking-wider pb-3 pr-6">
                  <span className="inline-flex items-center gap-1.5"><MousePointerClick size={13} /> Clicks</span>
                </th>
                <th className="text-[#888] text-xs font-sans font-medium uppercase tracking-wider pb-3 pr-6">
                  <span className="inline-flex items-center gap-1.5"><Eye size={13} /> Product Views</span>
                </th>
                <th className="text-[#888] text-xs font-sans font-medium uppercase tracking-wider pb-3 pr-6">
                  <span className="inline-flex items-center gap-1.5"><Layers size={13} /> Avg Swipe Depth</span>
                </th>
                <th className="text-[#888] text-xs font-sans font-medium uppercase tracking-wider pb-3">
                  <span className="inline-flex items-center gap-1.5"><ShoppingBag size={13} /> Buy Clicks</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((ev) => (
                <tr key={ev.eventId} className="border-b border-[#1a1a1a]" data-testid={`analytics-row-${ev.eventId}`}>
                  <td className="py-4 pr-6">
                    <p className="text-white text-sm font-sans font-medium">{ev.brand}</p>
                    <p className="text-[#555] text-xs font-sans">{ev.status}</p>
                  </td>
                  <td className="py-4 pr-6 text-white text-sm font-sans" data-testid={`clicks-${ev.eventId}`}>{ev.clicks}</td>
                  <td className="py-4 pr-6 text-white text-sm font-sans" data-testid={`views-${ev.eventId}`}>{ev.productViews}</td>
                  <td className="py-4 pr-6 text-white text-sm font-sans" data-testid={`depth-${ev.eventId}`}>{ev.avgSwipeDepth}</td>
                  <td className="py-4 text-[#D8C08F] text-sm font-sans font-medium" data-testid={`buys-${ev.eventId}`}>{ev.buyClicks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
